/**
 * v7.56 — Wrappers da Places API (New) usando ENDPOINTS REST diretos.
 *
 * Por que REST e não a JS SDK?
 *   - A New API REST (`places.googleapis.com/v1/...`) funciona perfeitamente
 *     com chaves restritas por HTTP referer (testado em produção).
 *   - O SDK JS depende do PlaceAutocompleteElement (Custom Element) que
 *     só está disponível em `v=weekly` recente. Quando o Custom Element
 *     não estiver disponível (ou o SDK tiver bug), usamos REST como fallback.
 *
 * Endpoints usados:
 *   - POST /v1/places:searchText  → busca por texto livre + retorna location
 *   - POST /v1/places:autocomplete → predições enquanto o usuário digita
 *
 * Header obrigatório: `X-Goog-FieldMask` (especifica campos a retornar)
 * + `X-Goog-Api-Key` (chave da API).
 */

const ENDPOINT_SEARCH = 'https://places.googleapis.com/v1/places:searchText';
// Campos retornados pelo searchText (FieldMask). Quanto menos campos, menor o custo.
const SEARCH_FIELD_MASK = [
  'places.id',
  'places.displayName',
  'places.formattedAddress',
  'places.location',
  'places.addressComponents',
].join(',');

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

/**
 * Faz GEOCODING de um endereço em texto livre usando a Places API (New).
 * Retorna o primeiro place encontrado com lat/lng + componentes do endereço.
 *
 * Útil quando:
 *   - O cliente digitou o endereço manualmente (sem selecionar do autocomplete)
 *   - O SDK JS do Maps falhou ao carregar
 *   - Precisamos validar a área de entrega antes do checkout
 *
 * @param {string} query - endereço livre (ex: "Rua Marta Vidal, 301, Fragoso, Magé")
 * @param {object} [opts]
 * @param {{lat:number,lng:number}} [opts.bias] - viés de localização (próximo do restaurante)
 * @param {number} [opts.biasRadiusMeters=15000] - raio do viés
 * @returns {Promise<{ok:boolean, place?:{formattedAddress, lat, lng, components}, error?:string}>}
 */
export async function geocodeViaPlacesNew(query, opts = {}) {
  if (!API_KEY) return { ok: false, error: 'NO_API_KEY' };
  if (!query || !query.trim()) return { ok: false, error: 'EMPTY_QUERY' };

  const body = {
    textQuery: query.trim(),
    languageCode: 'pt-BR',
    regionCode: 'BR',
    maxResultCount: 1,
  };
  // Location bias melhora MUITO a relevância quando o cliente digita
  // só "rua tal" sem cidade. Passamos as coords do restaurante + raio
  // generoso (15km default) pra priorizar locais ao redor.
  if (opts.bias && Number.isFinite(opts.bias.lat) && Number.isFinite(opts.bias.lng)) {
    body.locationBias = {
      circle: {
        center: { latitude: opts.bias.lat, longitude: opts.bias.lng },
        radius: opts.biasRadiusMeters || 15000,
      },
    };
  }

  // v7.74 -- Timeout explicito via AbortController. Sem isso, um fetch que
  // trava (rede ruim, API lenta, device sobrecarregado) deixava o checkout
  // preso pra sempre em "Validando endereco..." -- reportado no ticket
  // #207625 como "loop infinito" na validacao de endereco.
  const attemptOnce = async () => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 7000);
  try {
    const resp = await fetch(ENDPOINT_SEARCH, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': API_KEY,
        'X-Goog-FieldMask': SEARCH_FIELD_MASK,
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    if (!resp.ok) {
      let errMsg = `HTTP_${resp.status}`;
      try {
        const errJson = await resp.json();
        errMsg = errJson?.error?.message || errMsg;
      } catch (_) { /* ignore parse error */ }
      return { ok: false, error: errMsg };
    }
    const data = await resp.json();
    const place = data.places?.[0];
    if (!place) {
      return { ok: false, error: 'NO_RESULTS' };
    }
    return {
      ok: true,
      place: {
        formattedAddress: place.formattedAddress || place.displayName?.text || query,
        lat: place.location?.latitude,
        lng: place.location?.longitude,
        components: (place.addressComponents || []).map((c) => ({
          long_name: c.longText || '',
          short_name: c.shortText || '',
          types: c.types || [],
        })),
      },
    };
  } catch (err) {
    if (err?.name === 'AbortError') {
      return { ok: false, error: 'TIMEOUT', retriable: true };
    }
    return { ok: false, error: err?.message || 'NETWORK_ERROR', retriable: true };
  } finally {
    clearTimeout(timeoutId);
  }
  };

  // v7.82 (ticket 207625) - reintenta 1x em falha transitoria (timeout/rede).
  // Blips da Places API / rede movel faziam o cliente repetir "Validando
  // endereco..." varias vezes. 7s+7s cabe no timeout duro de 15s do checkout.
  let result = await attemptOnce();
  if (!result.ok && result.retriable) {
    result = await attemptOnce();
  }
  const { retriable, ...clean } = result;
  void retriable;
  return clean;
}

const ENDPOINT_AUTOCOMPLETE = 'https://places.googleapis.com/v1/places:autocomplete';

/**
 * TICKET 207625 - Autocomplete (predicoes) via Places API (New) REST.
 * Usado pelo AddressAutocomplete quando o Web Component PlaceAutocompleteElement
 * nao carrega (modo "manual"): em vez de o cliente digitar as cegas e geocodar
 * so no submit (lento/instavel), mostramos uma LISTA de enderecos pra ele
 * escolher -- exatamente o que o cliente pediu no ticket.
 * @returns {Promise<{ok:boolean, predictions?:Array<{placeId:string,text:string}>, error?:string}>}
 */
export async function autocompleteViaPlacesNew(query, opts = {}) {
  if (!API_KEY) return { ok: false, error: 'NO_API_KEY' };
  const q = (query || '').trim();
  if (q.length < 4) return { ok: true, predictions: [] };
  const body = { input: q, languageCode: 'pt-BR', includedRegionCodes: ['br'] };
  if (opts.bias && Number.isFinite(opts.bias.lat) && Number.isFinite(opts.bias.lng)) {
    body.locationBias = {
      circle: {
        center: { latitude: opts.bias.lat, longitude: opts.bias.lng },
        radius: opts.biasRadiusMeters || 15000,
      },
    };
  }
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 6000);
  try {
    const resp = await fetch(ENDPOINT_AUTOCOMPLETE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': API_KEY,
        'X-Goog-FieldMask': 'suggestions.placePrediction.placeId,suggestions.placePrediction.text',
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    if (!resp.ok) return { ok: false, error: `HTTP_${resp.status}` };
    const data = await resp.json();
    const predictions = (data.suggestions || [])
      .map((s) => s.placePrediction)
      .filter(Boolean)
      .map((p) => ({ placeId: p.placeId, text: (p.text && p.text.text) || '' }))
      .filter((p) => p.text);
    return { ok: true, predictions };
  } catch (err) {
    if (err?.name === 'AbortError') return { ok: false, error: 'TIMEOUT' };
    return { ok: false, error: err?.message || 'NETWORK_ERROR' };
  } finally {
    clearTimeout(timeoutId);
  }
}
